import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'hooks/apollo';
import Table from 'components/Table';
import LanguageTag from 'components/LanguageTag';
import Loading from 'components/Loading';
import { Input, Button, Form, Switch, Col, Row } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import RichTextInput from 'components/RichTextInput';
import Modal from 'components/Modal';

import CustomerSelect from 'components/CustomerSelect';
import SurveyGroupSelect from 'components/SurveyGroupSelect';
import { gql } from '@apollo/client';
import LanguageSelect from 'components/LanguageSelect';

const TRANSLATIONS_QUERY = gql`
  query SurveyTranslations($subKey: String!) {
    translations(subKey: $subKey) {
      key
      value
    }
  }
`;

const DEFAULT_TRANSLATIONS_QUERY = gql`
  query DefaultTranslations {
    surveyTranslations {
      key
      text
    }
    languages {
      id
      name
      supported
    }
    defaultTranslations: translations(subKey: "__SurveyApp__") {
      key
      value
    }
  }
`;

const SET_TRANSLATIONS_MUTATION = gql`
  mutation($translations: [TranslationCreateInput!]) {
    setTranslations(translations: $translations)
  }
`;

const DEFAULT_KEY = 'SurveyApp';
const DEFAULT_LANGUAGE = {
  name: 'English',
  id: 'en-GB',
};

const EditTranslationModal = ({ translationToEdit, subKey, onDone, onCancel }) => {
  const { surveyTranslation, defaultTranslation, language, translation } = translationToEdit;
  const [richTextEditing, setRichTextEditing] = useState(translation ? translation.value?.match(/<[^>]*>/g) : defaultTranslation?.value?.match(/<[^>]*>/g));
  const [mutate, { loading }] = useMutation(SET_TRANSLATIONS_MUTATION);
  const [form] = Form.useForm();

  const onSubmit = async ({ value }) => {
    await mutate({ variables: { translations: [{ key: `${surveyTranslation.key}__${subKey}__text__${language.id}`, value }] } });
    onDone();
  };

  return (
    <Modal
      title="Edit translation"
      fullWidth
      centered
      open
      confirmLoading={loading}
      okText="Save"
      onOk={form.submit}
      onCancel={onCancel}
    >
      <Form layout="vertical" form={form} onFinish={onSubmit} initialValues={{ value: translation?.value }}>
        <Row gutter={16}>
          <Col span={12}>
            <h3>
              <LanguageTag languageName={defaultTranslation.language.name} locale={defaultTranslation.language.id} />
              Default text
            </h3>
            <p style={{ maxHeight: '40vh', overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: defaultTranslation.value }} />
          </Col>
          <Col span={12}>
            <h3>
              <LanguageTag languageName={language.name} locale={language.id} />
              Translation
              <Switch checkedChildren="Rich text" unCheckedChildren="Plain text" checked={richTextEditing} onChange={setRichTextEditing} style={{ marginLeft: '1em' }} />
            </h3>
            <Form.Item name="value">
              {richTextEditing ? <RichTextInput /> : <Input.TextArea autoSize={{ maxRows: 20 }} />}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const SurveyTranslations = () => {
  const [search, setSearch] = useState();
  const [customerId, setCustomerId] = useState();
  const [surveyId, setSurveyId] = useState();
  const [translationToEdit, setTranslationToEdit] = useState();
  const [selectedLocales, setSelectedLocales] = useState(null);

  const subKey = surveyId || customerId || DEFAULT_KEY;

  const isDefaultTranslations = subKey === DEFAULT_KEY;

  const { data = {}, loading: translationsLoading, refetch } = useQuery(TRANSLATIONS_QUERY, { variables: { subKey: `__${subKey}__`, skip: isDefaultTranslations } });
  const { data: defaultTranslationsData = {}, loading: defaultTranslationsLoading } = useQuery(DEFAULT_TRANSLATIONS_QUERY);

  let { translations = [] } = data;

  const { languages = [], surveyTranslations = [], defaultTranslations = [] } = defaultTranslationsData;

  const loading = translationsLoading || defaultTranslationsLoading;

  useEffect(() => {
    if (languages.length > 0 && selectedLocales == null) {
      setSelectedLocales(languages.filter(l => l.supported).map(l => l.id));
    }
  }, [languages]);

  if (loading) {
    return <Loading />;
  }

  if (isDefaultTranslations) {
    translations = defaultTranslations;
  }

  const selectedLanguages = languages.filter(l => selectedLocales?.find(sl => sl === l.id));

  const columns = [
    {
      dataIndex: 'key',
      title: 'Key',
      fixed: 'left',
      width: 300,
    },
    {
      dataIndex: 'text',
      title: 'Default text',
      visible: false,
      fixed: 'left',
      width: 500,
    },
  ];

  columns.push(...selectedLanguages.map(language => ({
    dataIndex: language.id,
    title: (
      <LanguageTag languageName={language.name} locale={language.id} />
    ),
    align: 'center',
    render: ({ defaultTranslation, translation }, surveyTranslation) => (
      <Button
        size="small"
        type={translation ? 'primary' : 'default'}
        icon={translation ? <EditOutlined /> : <PlusOutlined />}
        onClick={() => setTranslationToEdit({ surveyTranslation, defaultTranslation, translation, language })}
      >
        {translation ? 'Edit' : 'Add'}
      </Button>
    ),
    sorter: false,
  })));

  function getDefaultTranslation(surveyTranslation, language) {
    if (!isDefaultTranslations) {
      const defaultTranslation = defaultTranslations.find(t => t.key.includes(`${surveyTranslation.key}__`) && t.key.includes(`__${language.id}`));

      if (defaultTranslation) {
        return {
          value: defaultTranslation.value,
          language,
        };
      }
    }

    return {
      value: surveyTranslation.text,
      language: DEFAULT_LANGUAGE,
    };
  }

  const dataSource = surveyTranslations.map(st => ({
    ...st,
    ...selectedLanguages.reduce((obj, sl) => {
      obj[sl.id] = {
        defaultTranslation: getDefaultTranslation(st, sl),
        translation: translations.find(t => t.key.includes(`${st.key}__`) && t.key.includes(`__${sl.id}`)),
      };
      return obj;
    }, {}),
  }));

  const onDone = () => {
    setTranslationToEdit(false);
    refetch();
  };

  const handleCustomerChange = (id) => {
    setCustomerId(id);
    setSurveyId(null);
  };

  return (
    <>
      {translationToEdit && <EditTranslationModal translationToEdit={translationToEdit} subKey={subKey} onCancel={() => setTranslationToEdit(false)} onDone={onDone} />}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <LanguageSelect mode="multiple" value={selectedLocales} onChange={setSelectedLocales} style={{ width: '100%' }} />
        </Col>
        <Col>
          <CustomerSelect value={customerId} onChange={handleCustomerChange} allowClear />
        </Col>
        <Col>
          <SurveyGroupSelect value={surveyId} onChange={setSurveyId} customerId={customerId} disabled={!customerId} showStatus={false} />
        </Col>
        <Col flex={1}>
          <Input.Search onSearch={setSearch} allowClear placeholder="Search" />
        </Col>
        <Col span={24}>
          <Table
            rowKey="key"
            rowKeyColumn={false}
            bordered
            columns={columns}
            dataSource={dataSource}
            search={search}
            scroll={{ x: 1300 }}
            interactive={false}
          />
        </Col>
      </Row>
    </>
  );
};

export default SurveyTranslations;
